import React from 'react';
import _ from 'underscore';
// @ts-ignore
import { Constants } from '@bingads-webui-clientcenter/entity-utils';
import {
  DismissMode,
  INotification,
  NotificationLevel,
  ScopeLevel,
  TargetComponent
} from '../../types/notification.types';
import { IContext } from '../../types/context.types';
import { i18nMap, shouldRender, calcScopeLevel, isBillingLinkPermission as isBillingLinkPermissionFunc } from '../../utils';

export class PredefinedNotifications {
  context: IContext;
  i18n: any;

  constructor(context: IContext) {
    this.context = context;
    this.i18n = context.getNewI18n();
  }

  async getPredefinedAlerts(): Promise<(INotification | null)[]> {
    const accountSuspendedAlert = this.getAccountSuspendedAlert();
    const adBlockAlert = await this.getAdBlockAlert();
    const pmaxImportNotification = await this.getPerfMaxImportInfoNotification();
    const pmaxNonRetailExpansionNotification = await this.getPMaxRetailToNonRetailExpansionNotification();
    const pmaxNewAspectRatioNotification = await this.getPMaxNewAspectRatioNotification();
    const isExternalUserBillingLinkPermission = !this.context.getUserContext().CurrentUser.IsInternal && isBillingLinkPermissionFunc(this.context.getSettingsContext());

    return _.compact([
      !isExternalUserBillingLinkPermission && this.getFixAudienceCampaignImportedAsSearchCampaignNotification(),
      !isExternalUserBillingLinkPermission && this.getGoogleImportImageAdForScheduledImportAccountsNotification(),
      !isExternalUserBillingLinkPermission && this.getNotificationForCarouselAdImport(),
      !isExternalUserBillingLinkPermission && this.getMmaV2LaunchNotification(),
      !isExternalUserBillingLinkPermission && this.getAdoptAudienceShoppingCampaignNotification(),
      !isExternalUserBillingLinkPermission && this.getManualCpcDeprecationNotification(),
      !isExternalUserBillingLinkPermission && pmaxImportNotification,
      pmaxNonRetailExpansionNotification,
      pmaxNewAspectRatioNotification,
      this.getVersionRefreshedAlert(),
      adBlockAlert,
      accountSuspendedAlert,
      this.getMaintainanceAlert(),
      this.getIncompatibleBrowserAlert(),
      !isExternalUserBillingLinkPermission && this.getWebInsightsNotification(),
      !isExternalUserBillingLinkPermission && this.getGAConversionAttributionNotification(),
      !isExternalUserBillingLinkPermission && this.getMSANAutoBiddingInfoNotification(),
      !isExternalUserBillingLinkPermission && this.getSmartCropOfflineProcessingNotification(),
      ...(!isExternalUserBillingLinkPermission ? this.getMSANChurnNotifications() : []),
      !isExternalUserBillingLinkPermission && this.getImpressionReportingNotification(),
      !isExternalUserBillingLinkPermission && this.getMSANALogoAndCTAGANotification(),
    ]);
  }

  async getAdBlockAlert() {
    const scopeLevel = ScopeLevel.root;
    if (!shouldRender(scopeLevel, this.context.getChangeScope())) {
      return null;
    }

    const isAdBlockOn = await this.context.isAdBlockOn();
    const adBlockAlert: INotification = {
      condition: isAdBlockOn,
      id: 'Alert_AdBlockWarning',
      dismissMode: DismissMode.permanent,
      target: TargetComponent.TopBanner,
      severity: NotificationLevel.Warning,
      scopeLevel,
      title: '',
      message: this.i18n.getString(i18nMap.UnifiedNotificationQueue_Alert_AdBlockWarning_Message),
      actionList: [
        {
          text: this.i18n.getString(i18nMap.LearnMore),
          extraClassNames: ['HelpTopic'],
          extraAttrs: {
            query: 'help:56790',
          },
        },
      ],
    };

    return adBlockAlert;
  }

  getGAConversionAttributionNotification() {
    const accountMode = this.context.getUserContext().CurrentAccount.AccountMode;
    if (accountMode !== 0 && accountMode !== null) {
      return null;
    }

    return {
      condition: true,
      id: 'GAConversionAttribution',
      target: TargetComponent.UnifiedQueue,
      dismissMode: DismissMode.permanent,
      severity: NotificationLevel.Info,
      scopeLevel: ScopeLevel.account,
      title: this.i18n.getString(_TL_('Changes to Google Analytics conversion attribution')),
      message: this.i18n.getString(_TL_('Google\'s recent move to a default data driven attribution model in the new Google Analytics 4 (GA4) may result in significant discrepancies in conversion attribution reporting when compared to Microsoft Advertising. If you feel your Microsoft Advertising reporting may be impacted by GA4\'s new default attribution model, contact support to better understand your conversion and revenue reporting within Microsoft Advertising.')),
      actionList: [
        {
          text: this.i18n.getString(_TL_('Learn more')),
          action: () => {
            window.open('https://help.ads.microsoft.com/#apex/ads/en/56841/2', '_blank');
          },
          callbackType: 'closeQueue',
          extraClassNames: undefined,
          extraAttrs: undefined,
        },
        {
          text: this.i18n.getString(_TL_('Contact support')),
          action: () => {
            window.open('https://about.ads.microsoft.com/en-us/microsoft-advertising-support', '_blank');
          },
          callbackType: 'closeQueue',
          extraClassNames: undefined,
          extraAttrs: undefined,
        },
      ],
    };
  }

  async getPMaxRetailToNonRetailExpansionNotification() {
    const permissions = _.result(this.context.getCurrentPermissions(), 'dynamic', {});

    if (!_.result(permissions, 'PMaxRetailToNonRetailExpansionNotification', false) || this.context.getUserContext().CurrentAccount.AccountMode === 2) {
      // Skip notification if pilot is not enabled for this account, or if account mode is 2 (UNIFIED_SMART)
      return null;
    }

    return {
      condition: true,
      id: 'PMaxRetailToNonRetailExpansion',
      target: TargetComponent.UnifiedQueue,
      dismissMode: DismissMode.permanent,
      severity: NotificationLevel.Info,
      scopeLevel: ScopeLevel.account,
      title: this.i18n.getString(_TL_('Updates to allow auto asset generation')),
      message: this.i18n.getString(_TL_('We recently made updates that allow assets to generate automatically for some of your Pmax campaigns that have auto asset generation enabled in campaign settings. After this fix, your Performance Max campaigns will begin creating new text and image assets, alongside current product ads, resulting in more impressions and clicks.')),
      actionList: [
        {
          text: this.i18n.getString(_TL_('Learn more')),
          action: () => {
            window.open('https://help.ads.microsoft.com/apex/index/3/en/60058', '_blank');
          },
          callbackType: 'closeQueue',
          extraClassNames: undefined,
          extraAttrs: undefined,
        },
      ],
    };
  }

  async getPMaxNewAspectRatioNotification() {
    const permissions = _.result(this.context.getCurrentPermissions(), 'dynamic', {});

    if (!_.result(permissions, 'PMaxNewAspectRatiosNotification', false)) {
      // Skip notification if permission is not granted for this account
      return null;
    }

    if (!_.result(permissions, 'PMaxNewAspectRatios', false) || _.result(permissions, 'PMaxExcludeNewAspectRatios', false)) {
      // Skip notification if pilot is not enabled for this account
      return null;
    }

    const config = {
      condition: true,
      id: 'PMaxNewAspectRatioNotification',
      target: TargetComponent.UnifiedQueue,
      dismissMode: DismissMode.permanent,
      severity: NotificationLevel.Info,
      scopeLevel: ScopeLevel.account,
      title: this.i18n.getString(_TL_('New and updated aspect ratios for Performance Max campaigns')),
      messageType: 'ReactComponent',
      actionList: [
        {
          text: this.i18n.getString(_TL_('Learn more')),
          action: () => {
            window.open('https://help.ads.microsoft.com/#apex/ads/en/60176/0', '_blank');
          },
          callbackType: 'closeQueue',
          extraClassNames: undefined,
          extraAttrs: undefined,
        },
      ],
    };

    const MessageBox = () => (
      <React.Fragment>
        {this.i18n.getString(_TL_('We updated the available aspect ratios for image assets in your Performance Max campaigns:'))}
        <ul className="pmax-new-aspect-ratios-list" style={{ marginBottom: '0px' }}>
          <li><b>{this.i18n.getString(_TL_('New aspect ratios: '))}</b>{this.i18n.getString(_TL_('2.01:1, 1.69:1, 1.53:1, and 1.24:1.'))}</li>
          <li><b>{this.i18n.getString(_TL_('Aspect ratios that are no longer supported: '))}</b>{this.i18n.getString(_TL_('1.78:1, 1.33:1, and 1.5:1.'))}</li>
        </ul>
        {this.i18n.getString(_TL_('To start serving images in the new aspect ratios for existing campaigns, you\'ll need to edit your asset group in '))}
        <b>{this.i18n.getString(_TL_('Campaigns > Asset groups'))}</b>
        {this.i18n.getString(_TL_('. Then, select '))}<b>{this.i18n.getString(_TL_('Include'))}</b>{this.i18n.getString(_TL_(' in the ad preview area for all the new aspect ratios.'))}
      </React.Fragment>
    );

    return {
      ...config,
      message: MessageBox,
    };
  }

  async getPerfMaxImportInfoNotification(getEntityFunctionObject?: any) {
    const permissions = _.result(this.context.getCurrentPermissions(), 'dynamic', {});

    if (!_.result(permissions, 'ShowPerfMaxImportNotification', false) || !_.result(permissions, 'ImportPerformanceMaxCampaignAsSmartShopping', false) || _.result(permissions, 'PerformanceMaxCampaigns', false)) {
      return null;
    }

    // skip for account mode 2 (UNIFIED_SMART)
    if (this.context.getUserContext().CurrentAccount.AccountMode === 2) {
      return null;
    }

    if (_.result(permissions, 'ImportNonRetailPMaxCampaignAsDynamicSearch', false)) {
      return {
        condition: true,
        id: 'perfMaxImportAsSmartShoppingOrSearch',
        target: TargetComponent.UnifiedQueue,
        dismissMode: DismissMode.permanent,
        severity: NotificationLevel.Info,
        scopeLevel: ScopeLevel.account,
        title: this.i18n.getString(_TL_('Import of Google Ads Performance Max campaigns')),
        message: this.i18n.getString(_TL_('You can now import your Google Ads Performance Max campaigns as smart shopping campaigns or search campaigns. If you added a Merchant Center account during Google Ads campaign creation, we’ll import your Performance Max campaigns as smart shopping campaigns; but, without a Merchant Center account, we’ll import them as search campaigns. ')),
        actionList: [
          {
            text: this.i18n.getString(_TL_('Start your import')),
            action: () => {
              this.context.route.goGoogleImport();
            },
            callbackType: 'closeQueue',
            extraClassNames: undefined,
            extraAttrs: undefined,
          },
          {
            text: this.i18n.getString(_TL_('Learn more')),
            action: () => {
              window.open('https://go.microsoft.com/fwlink/?linkid=2208229', '_blank');
            },
            callbackType: 'closeQueue',
            extraClassNames: undefined,
            extraAttrs: undefined,
          },
        ],
      };
    }

    const hasSmartShoppingCampaign = await this.hasSmartShoppingCampaign(
      this.context,
      getEntityFunctionObject
    );

    if (!hasSmartShoppingCampaign) {
      return null;
    }

    return {
      condition: true,
      id: 'perfMaxImportAsSmartShopping',
      target: TargetComponent.UnifiedQueue,
      dismissMode: DismissMode.permanent,
      severity: NotificationLevel.Info,
      scopeLevel: ScopeLevel.account,
      title: this.i18n.getString(_TL_('Import of Google Ads Performance Max campaigns')),
      message: this.i18n.getString(_TL_('We’ll import your Google Ads Performance Max campaigns as smart shopping campaigns.')),
      actionList: [
        {
          text: this.i18n.getString(_TL_('Start your import')),
          action: () => {
            this.context.route.goGoogleImport();
          },
          callbackType: 'closeQueue',
          extraClassNames: undefined,
          extraAttrs: undefined,
        },
        {
          text: this.i18n.getString(_TL_('Learn more')),
          action: () => {
            window.open('https://go.microsoft.com/fwlink/?linkid=2208229', '_blank');
          },
          callbackType: 'closeQueue',
          extraClassNames: undefined,
          extraAttrs: undefined,
        },
      ],
    };
  }

  getWebInsightsNotification() {
    const permissions = _.result(this.context.getCurrentPermissions(), 'dynamic', {});
    const scope = this.context.getCurrentScope();

    const { aid } = scope.values;
    const serverContext = this.context.getServerContext();
    const message = `${this.i18n.getString(_TL_('UET Insights will show you key performance metrics to help you improve your website and audience targeting. These metrics can be found in your UET tag dashboard under the "Insights" tab. Enable UET Insights for all your tags, or enable it for individual tags by visiting the associated UET tag dashboard.'))} `;
    const learnMore = `<a href="https://help.ads.microsoft.com/#apex/ads/en/60177/-1" target="_blank" rel="noopener noreferrer" aria-label="${this.i18n.getString(_TL_('Learn More'))}">${this.i18n.getString(_TL_('Learn More'))}</a>`;

    if (!_.result(permissions, 'UETTagInsights', false) || !aid) {
      return null;
    }
    const edmCampaign = this.context.getEdmCampaign();

    // @ts-ignore-next-line
    const edmHolder = edmCampaign.Customers.$withKey(serverContext.CurrentAccount.AdvertiserCustomerID).Accounts.$withKey(aid);

    return {
      condition: true,
      id: 'WebInsightsNotification',
      target: TargetComponent.UnifiedQueue,
      dismissMode: DismissMode.permanent,
      severity: NotificationLevel.Info,
      scopeLevel: ScopeLevel.account,
      title: this.i18n.getString(_TL_('Opt-in to UET insights to get website traffic analytics through your UET Tag')),
      message: message + learnMore,
      actionList: [
        {
          text: this.i18n.getString(_TL_('Enable UET Insights for all my UET tags')),
          action: () => {
            const tagsPromise = edmHolder
              .Tags
              .get({ top: 200, select: ['Id', 'IsWebInsightsEnabled'] }).toPromise();

            return tagsPromise.then((tagResponse) => {
              if (tagResponse && tagResponse.value) {
                if (tagResponse.value.length > 0) {
                  const tags = tagResponse.value;
                  const payload = tags.reduce((acc, tag) => {
                    if (!tag.IsWebInsightsEnabled) {
                      acc.push({ Id: tag.Id, IsWebInsightsEnabled: true });
                    }
                    return acc;
                  }, []);

                  if (payload.length === 0) {
                    return Promise.resolve(true);
                  }

                  return edmHolder
                    .Tags['Default.UpdateTagsWebInsightsSetting']
                    .$makeCall({ payload: { Tags: payload } })
                    .toPromise().then(() => true)
                    .catch((error) => {
                      throw new Error(error);
                    });
                }
              }
              return Promise.resolve(true);
            }).catch((error) => {
              throw new Error(`Could not fetch tags: ${error}`);
            });
          },
          callbackType: 'closeQueue',
          extraClassNames: undefined,
          extraAttrs: undefined,
        },
        {
          text: this.i18n.getString(_TL_('Go to UET tags')),
          action: () => {
            window.open(`/campaign/vnext/uettag?uid=${scope.values.uid}&cid=${scope.values.cid}&aid=${scope.values.aid}`, '_self');
          },
          callbackType: 'closeQueue',
          extraClassNames: undefined,
          extraAttrs: undefined,
        },
      ],
    };
  }

  getMSANAutoBiddingInfoNotification() {
    const permissions = this.context.getCurrentPermissions();

    // if user is smart account, don't show the notification
    if (_.result(permissions, 'IsSmartAccount', false)) {
      return null;
    }

    return {
      condition: true,
      id: 'MSANAutoBiddingInfoNotification',
      target: TargetComponent.UnifiedQueue,
      dismissMode: DismissMode.permanent,
      severity: NotificationLevel.Info,
      scopeLevel: ScopeLevel.account,
      title: this.i18n.getString(_TL_('Multi-channel management update')),
      message: this.i18n.getString(_TL_('Starting February 21st, you will no longer see the audience ad bid modifier in your campaign settings. Microsoft Advertising will fully optimize your campaigns across channels, as machine learning algorithms drive performance to your campaign goals. There is nothing you need to do to prepare, but you should always ensure optimal settings (adopt UET tags/conversion goals, set your desired automated bid strategy, and add image extensions) to deliver the best performance. If you need assistance, please contact support or your Microsoft Advertising account team.')),
      actionList: [
        {
          text: this.i18n.getString(_TL_('Contact support')),
          action: () => {
            window.open('https://about.ads.microsoft.com/en-us/microsoft-advertising-support', '_blank');
          },
          callbackType: 'closeQueue',
          extraClassNames: undefined,
          extraAttrs: undefined,
        },
      ],
    };
  }

  getMSANALogoAndCTAGANotification() {
    const permissions = this.context.getCurrentPermissions();

    if (!_.result(permissions.dynamic, 'MSANLogoAndCTANotification', false)) {
      return null;
    }

    return {
      condition: true,
      id: 'MSANALogoAndCTAGANotification',
      target: TargetComponent.UnifiedQueue,
      dismissMode: DismissMode.permanent,
      severity: NotificationLevel.Info,
      scopeLevel: ScopeLevel.account,
      title: this.i18n.getString(_TL_('Logos and calls to action are available for Native ads')),
      message: this.i18n.getString(_TL_('You can enhance your Native ads and entice customers to click them by adding logos and calls to action during ad creation. For logos, simply upload or choose an image for your logo from the Asset library. For calls to action, choose from our predefined list. This will provide potential customers with a brief, compelling reason to click your ad. </br> Note: <b>Automated</b> is the default option for your call to action. If you don\'t choose a call to action, it\'ll be automated by default during ad delivery. This means that we\'ll automatically choose your call to action and the language it serves in from the predefined list.')),
      actionList: [
        {
          text: this.i18n.getString(_TL_('Learn more')),
          action: () => {
            window.open('https://help.ads.microsoft.com/#apex/ads/en/56907/0', '_blank');
          },
          callbackType: 'closeQueue',
          extraClassNames: undefined,
          extraAttrs: undefined,
        },
      ],
    };
  }

  getSmartCropOfflineProcessingNotification() {
    const permissions = this.context.getCurrentPermissions();

    if (!_.result(permissions.dynamic, 'SmartCropNotification', false)) {
      return null;
    }

    return {
      condition: true,
      id: 'SmartCropOfflineProcessingNotification',
      target: TargetComponent.UnifiedQueue,
      dismissMode: DismissMode.permanent,
      severity: NotificationLevel.Info,
      scopeLevel: ScopeLevel.account,
      title: this.i18n.getString(_TL_('We’ve enabled smart image cropping for your account')),
      message: this.i18n.getString(_TL_('To help save time and increase efficiency, our AI will automatically crop your audience ad images and image extensions to focus on the most important part of the image. We recommend you review your existing audience ads and image extensions to ensure that your images and the aspect ratios they serve in look the way you want. Note: Smart image cropping will be applied automatically to any existing audience ad images and image extensions that you have not yet manually cropped.')),
      actionList: [
        {
          text: this.i18n.getString(_TL_('Learn more')),
          action: () => {
            window.open('https://help.ads.microsoft.com/#apex/ads/en/60202/1', '_blank');
          },
          callbackType: 'closeQueue',
          extraClassNames: undefined,
          extraAttrs: undefined,
        },
      ],
    };
  }

  getManualCpcDeprecationNotification() {
    const permissions = this.context.getCurrentPermissions();

    if (!_.result(permissions, 'ShouldDisplayManualCpcDeprecationNotification', false)) {
      return null;
    }

    return {
      condition: true,
      id: 'ManualCpcDeprecation1',
      target: TargetComponent.UnifiedQueue,
      dismissMode: DismissMode.permanent,
      severity: NotificationLevel.Info,
      scopeLevel: ScopeLevel.account,
      title: this.i18n.getString(_TL_('Update to Native ad campaign bid strategies')),
      message: this.i18n.getString(_TL_('Starting on April 30, 2024, you’ll no longer have the option to select the Manual CPC (cost per click) bid strategy for newly created image-based and feed-based Native ad campaigns. On May 13, 2024, all existing Native ad campaigns using manual CPC will transition to the enhanced CPC bid strategy. This change will be reflected within one week. <br/> <br/> Note: This change does not impact Connected TV ads, Display ads, Native ads using the Manual CPM (cost per thousand impressions) bid strategy, or Video Ad campaigns.')),
      actionList: [
        {
          text: this.i18n.getString(_TL_('Learn more')),
          action: () => {
            window.open('https://about.ads.microsoft.com/en-us/blog/post/april-2024/upgrading-native-campaigns-to-enhanced-cpc', '_blank');
          },
          callbackType: 'closeQueue',
          extraClassNames: undefined,
          extraAttrs: undefined,
        },
      ],
    };
  }

  getMmaV2LaunchNotification() {
    const permissions = this.context.getCurrentPermissions();

    if (!_.result(permissions, 'ShouldDisplayMMAV2LaunchNotification', false)) {
      return null;
    }

    return {
      condition: true,
      id: 'MMAV2LaunchNotification',
      target: TargetComponent.UnifiedQueue,
      dismissMode: DismissMode.permanent,
      severity: NotificationLevel.Info,
      scopeLevel: ScopeLevel.account,
      title: this.i18n.getString(_TL_('Update to ad preview for audience campaigns')),
      message: this.i18n.getString(_TL_('You can now preview more examples of what your audience ads might look like with our updated ad preview tool. To ensure that your audience ads are optimized for each ad placement and aspect ratio, review your ad previews and edit them accordingly.')),
      actionList: [
        {
          text: this.i18n.getString(_TL_('Learn more')),
          action: () => {
            window.open('https://help.ads.microsoft.com/#apex/ads/en/56912/0', '_blank');
          },
          callbackType: 'closeQueue',
          extraClassNames: undefined,
          extraAttrs: undefined,
        },
      ],
    };
  }

  getFixAudienceCampaignImportedAsSearchCampaignNotification() {
    const permissions = this.context.getCurrentPermissions();

    if (!_.result(permissions, 'NotificationForFixAudienceCampaignImportedAsSearchCampaign', false)) {
      return null;
    }
    const message = this.i18n.getString(_TL_('We’ve identified a system error, which has prevented a successful import of some of your audience campaigns from Google Ads. Three weeks after this notification’s send date, we’ll resolve this error for any subsequent imports you run. Please check your import logs on or after this date to ensure that your campaigns were imported successfully.'));

    const serverContext = this.context.getServerContext() || {};
    const rootURLPath = _.result(serverContext, 'Urls', {}).RootUIUrl;
    const aid = _.result(serverContext, 'CurrentAccount', {}).Id;
    const cid = _.result(serverContext, 'CurrentCustomer', {}).Id;
    const uid = _.result(serverContext, 'CurrentUser', {}).Id;

    const checkImportLogsUrl = rootURLPath && aid && cid && uid ?
      `${rootURLPath}campaign/vnext/importschedule?aid=${aid}&cid=${cid}&uid=${uid}` :
      'https://help.ads.microsoft.com/#apex/ads/en/50851/0'; // this is the default page for import description.

    return {
      condition: true,
      id: 'FixAudienceCampaignImportedAsSearchCampaignNotification',
      target: TargetComponent.UnifiedQueue,
      dismissMode: DismissMode.permanent,
      severity: NotificationLevel.Info,
      scopeLevel: ScopeLevel.account,
      title: this.i18n.getString(_TL_('We’ll fix your Audience campaign import')),
      message: `${message}`,
      actionList: [
        {
          text: this.i18n.getString(_TL_('Check import logs')),
          action: () => {
            window.open(`${checkImportLogsUrl}`, '_blank');
          },
          callbackType: 'closeQueue',
          extraClassNames: undefined,
          extraAttrs: {
            checkImportLogsUrl,
          },
        },
      ],
    };
  }

  getGoogleImportImageAdForScheduledImportAccountsNotification() {
    const permissions = this.context.getCurrentPermissions();

    if (!_.result(permissions, 'NotificationForImportGoogleImageAd', false)) {
      return null;
    }
    const message = this.i18n.getString(_TL_('We’ll begin supporting multi-format ads on October 9, at which point we’ll begin to import any image ads we’ve previously dropped from your scheduled Google import. Please review your ads once they’re in our system.'));

    const serverContext = this.context.getServerContext() || {};
    const rootURLPath = _.result(serverContext, 'Urls', {}).RootUIUrl;
    const aid = _.result(serverContext, 'CurrentAccount', {}).Id;
    const cid = _.result(serverContext, 'CurrentCustomer', {}).Id;
    const uid = _.result(serverContext, 'CurrentUser', {}).Id;

    const checkImportLogsUrl = rootURLPath && aid && cid && uid ?
      `${rootURLPath}campaign/vnext/campaigns?aid=${aid}&cid=${cid}&uid=${uid}` :
      'https://help.ads.microsoft.com/#apex/ads/en/50851/0'; // this is the default page for import description.

    return {
      condition: true,
      id: 'FixAudienceCampaignImportedAsSearchCampaignNotification',
      target: TargetComponent.UnifiedQueue,
      dismissMode: DismissMode.permanent,
      severity: NotificationLevel.Info,
      scopeLevel: ScopeLevel.account,
      title: this.i18n.getString(_TL_('Importing your multi-format ads')),
      message: `${message}`,
      actionList: [
        {
          text: this.i18n.getString(_TL_('View my campaigns')),
          action: () => {
            window.open(`${checkImportLogsUrl}`, '_blank');
          },
          callbackType: 'closeQueue',
          extraClassNames: undefined,
          extraAttrs: {
            checkImportLogsUrl,
          },
        },
      ],
    };
  }

  getNotificationForCarouselAdImport() {
    const permissions = this.context.getCurrentPermissions();
    if (!_.result(permissions, 'NotificationForCarouselAdImport', false)) {
      return null;
    }

    const serverContext = this.context.getServerContext() || {};
    const rootURLPath = _.result(serverContext, 'Urls', {}).RootUIUrl;
    const aid = _.result(serverContext, 'CurrentAccount', {}).Id;
    const cid = _.result(serverContext, 'CurrentCustomer', {}).Id;
    const uid = _.result(serverContext, 'CurrentUser', {}).Id;

    const startdate = new Date('2025-05-06');
    const checkImportLogsUrl = rootURLPath && aid && cid && uid ?
      `${rootURLPath}campaign/vnext/importschedule?aid=${aid}&cid=${cid}&uid=${uid}` :
      'https://help.ads.microsoft.com/#apex/ads/en/n5032/0'; // this is the default page for import description.

    return {
      condition: true,
      id: 'NotificationForCarouselAdImport',
      target: TargetComponent.UnifiedQueue,
      dismissMode: DismissMode.permanent,
      severity: NotificationLevel.Info,
      scopeLevel: ScopeLevel.account,
      title: this.i18n.getString(_TL_('Import your carousel ads as Native ads ')),
      message: this.i18n.getString(
        _TL_("Starting on {{startdate}}, we'll automatically import the first card of each Carousel ad included in your scheduled imports as Native ads. You can always adjust this option within Advanced settings."),
        {
          startdate: startdate.toLocaleDateString(),
        }
      ),
      actionList: [
        {
          text: this.i18n.getString(_TL_('View my scheduled imports')),
          action: () => {
            window.open(`${checkImportLogsUrl}`, '_blank');
          },
          callbackType: 'closeQueue',
          extraClassNames: undefined,
          extraAttrs: {
            checkImportLogsUrl,
          },
        },
      ],
    };
  }

  getAdoptAudienceShoppingCampaignNotification() {
    const permissions = this.context.getCurrentPermissions();
    if (!_.result(permissions, 'IsAWFShoppingAdoptionNotificationEnabled', false)) {
      return null;
    }
    const message = this.i18n.getString(_TL_('Reach people in the right place at the right time on any device by '));
    const messageLink = this.i18n.getString(_TL_('creating shopping audience campaigns.'));
    const learnMoreLink = 'https://www.youtube.com/watch?v=Ux8fIRFAkTQ';
    const config = {
      condition: true,
      id: 'AWFShoppingAdoptionNotification',
      target: TargetComponent.UnifiedQueue,
      dismissMode: DismissMode.permanent,
      severity: NotificationLevel.Info,
      scopeLevel: ScopeLevel.account,
      title: this.i18n.getString(_TL_('Create new shopping audience campaigns')),
      messageType: 'ReactComponent',
      actionList: [
        {
          text: this.i18n.getString(_TL_('Learn more')),
          action: () => {
            window.open(learnMoreLink, '_blank');
          },
          callbackType: 'closeQueue',
          extraClassNames: undefined,
          extraAttrs: undefined,
        },
      ],
    };
    const MessageBox = (props) => {
      // eslint-disable-next-line react/prop-types
      const { logger, scenarioNames, logContent } = props;
      const content = logContent(`id=${config.id}, title=${config.title}, message=${message}, notificationSource=, notificationTypeId=, uiDeliveryId=, src=message`);
      return (
        <React.Fragment>
          {message}
          <a
            href={learnMoreLink}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={messageLink}
            // eslint-disable-next-line react/prop-types
            onClick={() => logger.trace(scenarioNames.NotificationCard, content)}
          >
            {messageLink}
          </a>
        </React.Fragment>
      );
    };

    return {
      ...config,
      message: MessageBox,
    };
  }

  getImpressionReportingNotification() : INotification | null {
    return {
      condition: true,
      id: 'ImpressionReportingNotification',
      target: TargetComponent.UnifiedQueue,
      dismissMode: DismissMode.permanent,
      severity: NotificationLevel.Info,
      scopeLevel: ScopeLevel.account,
      title: this.i18n.getString(_TL_('Update to online video ad impressions reporting')),
      message: this.i18n.getString(_TL_('Your online video ad impressions now reflect viewable impressions, which are counted when at least 50% of the ad is in view on screen for two seconds or more (one second for native and display ads). This aligns with Interactive Advertising Bureau (IAB) standards.')),
    };
  }

  getMSANChurnNotifications() : INotification[] {
    const permissions = this.context.getCurrentPermissions();

    if (!_.result(permissions, 'IsMSANChurnNotificationEnabled', false)) {
      return [];
    }

    const phase = _.result(permissions, 'MSANChurnNotificationPhase', 0);

    const commonNotificationSetting = {
      target: TargetComponent.UnifiedQueue,
      dismissMode: DismissMode.permanent,
      severity: NotificationLevel.Info,
      scopeLevel: ScopeLevel.account,
      condition: true,
    };

    const churnNotifications : INotification[] = [
      _.extend({
        id: 'MSANChurnNotificationPhase1',
        title: this.i18n.getString(_TL_('Are your bids competitive enough to scale in your audience campaigns?')),
        message: this.i18n.getString(_TL_('Take a look at the bid landscape tool for recommendations and choose the optimal bid based on expected performance. Watch this quick video demo for more guidance.')),
        actionList: [
          {
            text: this.i18n.getString(_TL_('Video demo')),
            action: () => {
              window.open('https://youtu.be/cizFKB3yWuw', '_blank');
            },
            callbackType: 'closeQueue',
            extraClassNames: undefined,
            extraAttrs: undefined,
          },
          {
            text: this.i18n.getString(_TL_('Bid landscape tool')),
            action: () => {
              window.open('https://help.ads.microsoft.com/#apex/ads/en/51096/1', '_blank');
            },
            callbackType: 'closeQueue',
            extraClassNames: undefined,
            extraAttrs: undefined,
          },
        ],
      }, commonNotificationSetting),
      _.extend({
        id: 'MSANChurnNotificationPhase2',
        title: this.i18n.getString(_TL_('Are you giving your audience campaigns the best chance of success? ')),
        message: this.i18n.getString(_TL_('For any new audience campaigns, make sure your ads are running for enough time before evaluating ROI. This gives the algorithms a chance to learn about your audience and optimize for best performance.')),
        actionList: [
          {
            text: this.i18n.getString(_TL_('Optimization webcast')),
            action: () => {
              window.open('https://www.youtube.com/watch?v=Y6R_0cmF8GI', '_blank');
            },
            callbackType: 'closeQueue',
            extraClassNames: undefined,
            extraAttrs: undefined,
          },
        ],
      }, commonNotificationSetting),
      _.extend({
        id: 'MSANChurnNotificationPhase3',
        title: this.i18n.getString(_TL_('Do you have the best imagery for your audience campaigns?')),
        message: this.i18n.getString(_TL_('Upload multiple images and choose lifestyle photos for optimal performance. If you don\'t have images, you can import from Google, Facebook, or Pinterest, choose from our free stock images, or bring over images from your website.')),
        actionList: [
          {
            text: this.i18n.getString(_TL_('Best practices for images')),
            action: () => {
              window.open('https://help.ads.microsoft.com/#apex/ads/en/56912/0-500', '_blank');
            },
            callbackType: 'closeQueue',
            extraClassNames: undefined,
            extraAttrs: undefined,
          },
          {
            text: this.i18n.getString(_TL_('Using stock images')),
            action: () => {
              window.open('https://help.ads.microsoft.com/#apex/ads/en/60052/1', '_blank');
            },
            callbackType: 'closeQueue',
            extraClassNames: undefined,
            extraAttrs: undefined,
          },
          {
            text: this.i18n.getString(_TL_('Import tools')),
            action: () => {
              window.open('https://help.ads.microsoft.com/#apex/ads/en/n5032/0', '_blank');
            },
            callbackType: 'closeQueue',
            extraClassNames: undefined,
            extraAttrs: undefined,
          },
        ],
      }, commonNotificationSetting),
      _.extend({
        id: 'MSANChurnNotificationPhase4',
        title: this.i18n.getString(_TL_('Are you reaching your ideal customers in your audience campaigns?')),
        message: this.i18n.getString(_TL_('Here\'s some tips:\r\n\r\nSet up a separate campaign for each audience type – add the most relevant in market audiences in one campaign and remarketing in another.\r\n\r\nAdd positive bid boosts to the best performing segments – like location, day of week/time of day, device, and age/gender.')),
        actionList: [
          {
            text: this.i18n.getString(_TL_('Remarketing')),
            action: () => {
              window.open('https://help.ads.microsoft.com/#apex/ads/en/56935/1-500', '_blank');
            },
            callbackType: 'closeQueue',
            extraClassNames: undefined,
            extraAttrs: undefined,
          },
          {
            text: this.i18n.getString(_TL_('In-market audiences')),
            action: () => {
              window.open('https://help.ads.microsoft.com/#apex/ads/en/56851/1-500', '_blank');
            },
            callbackType: 'closeQueue',
            extraClassNames: undefined,
            extraAttrs: undefined,
          },
          {
            text: this.i18n.getString(_TL_('Target your ads')),
            action: () => {
              window.open('https://help.ads.microsoft.com/#apex/ads/en/51029/0-500', '_blank');
            },
            callbackType: 'closeQueue',
            extraClassNames: undefined,
            extraAttrs: undefined,
          },
        ],
      }, commonNotificationSetting),
      _.extend({
        id: 'MSANChurnNotificationPhase5',
        title: this.i18n.getString(_TL_('Are your potential customers experiencing creative fatigue from your audience ads?')),
        message: this.i18n.getString(_TL_('Your creative assets are what potential customers see first.\r\n\r\nRefresh your images regularly and add multiple variations of headlines, descriptions, and images for best performance.')),
        actionList: [
          {
            text: this.i18n.getString(_TL_('Best practices for images')),
            action: () => {
              window.open('https://help.ads.microsoft.com/#apex/ads/en/56912/0-500', '_blank');
            },
            callbackType: 'closeQueue',
            extraClassNames: undefined,
            extraAttrs: undefined,
          },
        ],
      }, commonNotificationSetting),
    ];

    const allowedNotifications : INotification[] = [];

    for (let i = 1; i < phase + 1; i++) {
      allowedNotifications.push(churnNotifications[i - 1]);
    }

    return allowedNotifications;
  }

  getVersionRefreshedAlert() {
    const versionRefreshedAlert: INotification = {
      condition: this.context.isVersionRefreshed(),
      id: 'versionRefreshed',
      target: TargetComponent.TopBanner,
      dismissMode: DismissMode.never,
      severity: NotificationLevel.Error,
      title: '',
      scopeLevel: ScopeLevel.root,
      message: this.i18n.getString(_TL_('We\'ve just made updates to Microsoft Advertising. You will need to refresh the page to get the new version. <a id=\'newVersion_refreshLink\' onClick=\'window.location.reload(true)\' tabindex=\'0\'>Refresh the page</a>')),
    };

    return versionRefreshedAlert;
  }

  getAccountSuspendedAlert() {
    const currentScopeLevel = calcScopeLevel(this.context.getCurrentScope());

    if (currentScopeLevel !== ScopeLevel.account) {
      return null;
    }

    const suspendedAccountStatus = Constants.AccountLifecycleStatus.Suspended;
    const accountSuspendedAlert: INotification = {
      condition: this.context.getUserContext()
        .CurrentAccount.Status === suspendedAccountStatus, // Account suspended
      id: 'accountSuspended',
      target: TargetComponent.TopBanner,
      dismissMode: DismissMode.never,
      severity: NotificationLevel.Error,
      title: '',
      scopeLevel: ScopeLevel.account,
      message: this.i18n.getString(i18nMap.Alert_Account_suspended),
    };

    return accountSuspendedAlert;
  }

  // FixMe: Fetch maintenance message from server side
  getMaintainanceAlert(): INotification | null {
    const scopeLevel = ScopeLevel.root;
    if (!shouldRender(scopeLevel, this.context.getChangeScope())) {
      return null;
    }

    return {
      condition: false, // this.config.get('ShowMaintenanceAlert'),
      id: 'DownForMaintenance_New',
      target: TargetComponent.UnifiedQueue,
      dismissMode: DismissMode.session,
      severity: NotificationLevel.Info,
      scopeLevel,
      title: this.i18n.getString(i18nMap.UnifiedNotificationQueue_DownForMaintenance_New_Title),
      message:
        this.i18n.getString(i18nMap.UnifiedNotificationQueue_DownForMaintenance_New_Message, {
          start: '', // this.config.get('ShowMaintenanceAlertStart'),
          end: '', // this.config.get('ShowMaintenanceAlertFinish')
        }),
    };
  }

  getIncompatibleBrowserAlert(): INotification | null {
    const scopeLevel = ScopeLevel.root;
    if (!shouldRender(scopeLevel, this.context.getChangeScope())) {
      return null;
    }

    return {
      condition: !this.context.isCompatibleBrowser(),
      id: 'Alert_IncompatibleBrowsers',
      target: TargetComponent.UnifiedQueue,
      dismissMode: DismissMode.session,
      severity: NotificationLevel.Info,
      scopeLevel,
      title: this.i18n.getString(i18nMap.UnifiedNotificationQueue_Alert_IncompatibleBrowsers_Title),
      message:
        this.i18n.getString(i18nMap.UnifiedNotificationQueue_Alert_IncompatibleBrowsers_Message),
      actionList: [
        {
          text: this.i18n.getString(i18nMap.LearnMore),
          callbackType: 'closeQueue',
          extraClassNames: ['HelpTopic'],
          extraAttrs: {
            query: 'help:52033',
          },
        },
      ],
    };
  }

  async hasSmartShoppingCampaign(context: IContext, getEntityFunctionObject: any) {
    const currentScope = context.getCurrentScope();
    const values = _.assign({}, currentScope.values);
    values.adGroupId = undefined;
    values.campaignId = undefined;

    const accountScope = {
      values,
      location: currentScope.location,
    };

    // @ts-ignore
    const { getEntityFromScope } = getEntityFunctionObject || await import(/* webpackMode: 'eager' */ '@bingads-webui/edm-campaign');
    const { currentEntity } = getEntityFromScope(accountScope);

    const query = 'CampaignType eq \'Shopping\' and CampaignSubType eq \'ShoppingSmartAds\'';
    const hasSmartShoppingCampaign = await currentEntity.Campaigns.get({
      top: 1,
      filter: {
        where: query,
      },
      select: ['Id'],
    }).fetch()
      .then(data => data.value.length > 0, () => false);

    return hasSmartShoppingCampaign;
  }
}
